import { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import { styles } from "../styles";
import { isNativeMobile } from "../utils/platform";
import { computeDashboardData } from "../dashboard/computeDashboardData";
import { TILE_REGISTRY, ALL_TILE_IDS } from "../dashboard/tileRegistry";
import { renderTile } from "../dashboard/renderTile";
import { loadLayout, saveLayout } from "../dashboard/layoutPersistence";
import { getDefaultLayoutForUser } from "../dashboard/defaultLayout";
import DashboardSidebar from "../dashboard/DashboardSidebar";
import DashboardQuadrant from "../dashboard/DashboardQuadrant";

const NARROW_BREAKPOINT_PX = 900;

export default function DashboardPage(props) {
  const { onShowEquipmentByStatus, uid, role, department } = props;
  const data = useMemo(() => computeDashboardData(props), [props]);

  // Responsive split: native mobile + narrow web both fall back to the
  // Phase 0 flat-grid layout. Desktop / tablet get the new sidebar +
  // quadrant. Resize listener keeps it reactive if the user drags the
  // window across the breakpoint.
  const [isNarrow, setIsNarrow] = useState(
    typeof window !== "undefined"
      ? window.innerWidth < NARROW_BREAKPOINT_PX
      : false
  );
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onResize = () =>
      setIsNarrow(window.innerWidth < NARROW_BREAKPOINT_PX);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const mobile = isNativeMobile() || isNarrow;

  // Per-user quadrant layout. Lazy-migration: loadLayout returns the
  // role/department default if the user has never saved one. The first
  // time the user changes a slot in edit mode, saveLayout writes their
  // doc -- after that, loadLayout returns the saved layout.
  const [layout, setLayout] = useState(() =>
    getDefaultLayoutForUser(role, department)
  );
  useEffect(() => {
    let cancelled = false;
    if (!uid) {
      setLayout(getDefaultLayoutForUser(role, department));
      return undefined;
    }
    loadLayout(db, uid, role, department).then((l) => {
      if (!cancelled) setLayout(l);
    });
    return () => {
      cancelled = true;
    };
  }, [uid, role, department]);

  // Customize mode -- toggled by the button above the quadrant. While
  // editing, each slot becomes a picker (DashboardQuadrant's EditableSlot);
  // changes save immediately so leaving edit mode never strands a
  // half-finished layout.
  const [isEditingLayout, setIsEditingLayout] = useState(false);

  const handleSetSlot = (slot, tileId) => {
    const next = {
      ...layout,
      quadrants: {
        ...(layout.quadrants || {}),
        [slot]: tileId || "",
      },
    };
    setLayout(next);
    if (uid) {
      saveLayout(db, uid, next).catch((err) =>
        console.warn("saveLayout failed:", err)
      );
    }
  };

  const handleResetLayout = () => {
    const def = getDefaultLayoutForUser(role, department);
    setLayout(def);
    if (uid) {
      saveLayout(db, uid, def).catch((err) =>
        console.warn("saveLayout reset failed:", err)
      );
    }
  };

  const equipmentHealthBlock = (
    <>
      <h3 style={{ marginTop: 0, marginBottom: "8px" }}>
        Equipment Health (opted-in buildings)
      </h3>
      {!data.hasAnyOptIn ? (
        <div
          style={{
            ...styles.card,
            fontSize: "13px",
            color: "#666",
            marginBottom: "20px",
          }}
        >
          No buildings opted in yet. Go to <strong>Connections</strong>,
          expand a customer, and check the buildings whose equipment health
          you want to monitor.
        </div>
      ) : (() => {
        // Pill-style health badges (matches LocationDetailPage compact look).
        // Caution + Disabled are tappable; click opens the filtered equipment list.
        const pill = (label, count, bg, color, onClick) => (
          <div
            style={{
              padding: "6px 12px",
              borderRadius: "12px",
              background: bg,
              color,
              border: `1px solid ${color}`,
              fontSize: "13px",
              fontWeight: 600,
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              cursor: onClick && count > 0 ? "pointer" : "default",
            }}
            onClick={() => onClick && count > 0 && onClick()}
          >
            <span>● {label}</span>
            <strong style={{ fontSize: "15px" }}>{count}</strong>
          </div>
        );
        return (
          <div
            style={{
              display: "flex",
              gap: "8px",
              flexWrap: "wrap",
              marginBottom: "20px",
            }}
          >
            {pill("Good", data.greenCount, "#c9efc9", "#1a7f37")}
            {pill("Caution", data.yellowCount, "#fff3cd", "#9a6b00", () =>
              onShowEquipmentByStatus &&
              onShowEquipmentByStatus("yellow", data.optedInEquipment)
            )}
            {pill("Disabled", data.redCount, "#f8d7da", "#c12c2c", () =>
              onShowEquipmentByStatus &&
              onShowEquipmentByStatus("red", data.optedInEquipment)
            )}
          </div>
        );
      })()}
    </>
  );

  if (mobile) {
    // Phase 0 behavior: flat grid of all visible tiles. Mobile users
    // don't get the sidebar/quadrant split -- the registry-driven flat
    // grid scales better on small screens.
    return (
      <>
        <h2>Dashboard</h2>
        {equipmentHealthBlock}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: "12px",
            marginBottom: "20px",
          }}
        >
          {ALL_TILE_IDS.map((id) => renderTile(id, data, props))}
        </div>
      </>
    );
  }

  // Desktop / tablet: sidebar of all 13 tiles + 2x2 quadrant of the
  // user's chosen four. The Customize / Reset controls live in the
  // sidebar's Personal section ("your homepage" feels personal, so
  // home-config settings belong next to My Time).
  return (
    <>
      <h2>Dashboard</h2>
      {equipmentHealthBlock}
      <div
        style={{
          display: "flex",
          gap: "20px",
          alignItems: "flex-start",
          marginBottom: "20px",
        }}
      >
        <DashboardSidebar
          data={data}
          dashboardProps={props}
          isEditingLayout={isEditingLayout}
          onToggleEdit={() => setIsEditingLayout((s) => !s)}
          onResetLayout={handleResetLayout}
        />
        <DashboardQuadrant
          layout={layout}
          data={data}
          dashboardProps={props}
          isEditing={isEditingLayout}
          onChangeSlot={handleSetSlot}
        />
      </div>
    </>
  );
}
